import React from 'react';
import { useAuth } from '../context/AuthContext';
import { orgAPI } from '../services/api';

const ALL = '';

// Drives the Unit/Team filter behavior for one feature's resolved
// access_level, used the same way across every page that filters by
// Unit/Team (Dashboard, Employees, Attendance, Reports, Productivity
// Rating, App Blocking):
//
//  - 'n/a'   -> no filters, no data. The page itself should also be hidden
//               from the sidebar (see Sidebar.js) — this hook just makes
//               sure it degrades safely even if reached directly.
//  - 'self'  -> no Unit/Team filters at all; data loads directly for the
//               caller.
//  - 'teams' -> Team filter only, options = caller's own teams. Exactly
//               one team: filter is hidden, that team is used directly.
//               More than one: filter shows, first team pre-selected.
//  - 'units' -> Unit + Team filters, options = caller's own units (and
//               every team within them) — cascades exactly like an
//               admin's full filter once there's more than one unit.
//               Exactly one unit: Unit filter is hidden and pre-selected
//               to it; Team filter still shows, cascading within it.
//  - 'all' (admin) -> full unrestricted Unit + Team filters, as before.
export default function useScopeFilters(featureKey) {
    const { isAdmin, accessLevel: resolveLevel } = useAuth();
    const level = isAdmin ? 'all' : resolveLevel(featureKey);

    const [units, setUnits]     = React.useState([]);
    const [teams, setTeams]     = React.useState([]);
    const [unitId, setUnitId]   = React.useState(ALL);
    const [teamId, setTeamId]   = React.useState(ALL);
    const [unitsReady, setUnitsReady] = React.useState(false);
    const [ready, setReady]     = React.useState(false);

    // Units — skipped entirely for 'self'/'n/a' (no filter needed at all)
    React.useEffect(() => {
        setUnitId(ALL);
        setUnitsReady(false);
        if (level === 'self' || level === 'n/a') { setUnits([]); setUnitsReady(true); setReady(true); return; }
        let cancelled = false;
        orgAPI.getUnits(featureKey).then((res) => {
            if (cancelled) return;
            const list = res.data.error !== '1' ? (res.data.units || []) : [];
            setUnits(list);
            if (level === 'units' && list.length === 1) setUnitId(list[0].unit_id);
            setUnitsReady(true);
        }).catch(() => { if (!cancelled) setUnitsReady(true); });
        return () => { cancelled = true; };
    }, [featureKey, level]);

    // Teams — reloads whenever unitId changes (admin/'units' cascade), or
    // once for 'teams' (not unit-scoped at all). Waits for units to settle
    // first so a single-unit 'units' caller doesn't fire an extra request.
    React.useEffect(() => {
        setTeamId(ALL);
        if (level === 'self' || level === 'n/a') { setTeams([]); return; }
        if (!unitsReady) return;
        let cancelled = false;
        const scopeUnitId = (level === 'units' || level === 'all') ? (unitId || undefined) : undefined;
        orgAPI.getTeams(scopeUnitId, featureKey).then((res) => {
            if (cancelled) return;
            const list = res.data.error !== '1' ? (res.data.teams || []) : [];
            setTeams(list);
            if (level === 'teams' && list.length >= 1) setTeamId(list[0].team_id);
            setReady(true);
        }).catch(() => { if (!cancelled) setReady(true); });
        return () => { cancelled = true; };
    }, [featureKey, level, unitId, unitsReady]);

    const showUnitFilter = level === 'all' || (level === 'units' && units.length > 1);
    const showTeamFilter = level === 'all' || level === 'units' || (level === 'teams' && teams.length > 1);

    return {
        level, ready, units, teams,
        unitId, setUnitId, teamId, setTeamId,
        showUnitFilter, showTeamFilter,
    };
}
